import { Card, Gate, Head } from "@authoxi/ui-shared";
import { apiBase } from "@authoxi/ui-shared";
import { client } from "../api";

/**
 * The verifiable receipt.
 *
 * **This page explains it and does not implement it**, and that is not laziness. The whole
 * signed event rides in the URL **fragment**, which browsers never send to a server — so the
 * receipt verifies in the recipient's browser and we never learn that they opened it. An SPA
 * shell for `/receipt` would mean a request, and the privacy property dies quietly the moment
 * it exists.
 *
 * So the real receipt stays backend-owned at `/receipt`, and this links to it.
 */
export function Receipt() {
  const base = apiBase();
  return (
    <>
      <Head
        pill="Verifiable receipt"
        title="A named human approved this agent to do this."
        lede="The entire signed event rides in the URL fragment, so it verifies in your browser and never reaches our server — we do not learn that you opened it. Forward it to a regulator, a customer, or a sceptic."
      />

      <Card pad="lg">
        <p className="label">How it works</p>
        <div className="steps" style={{ marginTop: "var(--s4)" }}>
          {[
            ["1", "A human approves", "They sign the exact action with their own Ed25519 key. We never hold it."],
            ["2", "The event becomes a link", "The whole signed decision is encoded into the URL fragment."],
            ["3", "The recipient opens it", "Their browser verifies it against the key inside the event itself."],
            ["4", "We learn nothing", "A fragment is never sent to a server. Not ours, not anyone's."],
          ].map(([n, t, b]) => (
            <div className="stepbox" key={n}>
              <span className="n">{n}</span>
              <div>
                <strong className="mono">{t}</strong>
                <p className="xs muted" style={{ margin: "3px 0 0", lineHeight: 1.45 }}>
                  {b}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="hr" />
        <p className="label">What a receipt does NOT say</p>
        <p style={{ margin: "var(--s3) 0 0", color: "var(--ink-2)", maxWidth: "var(--measure)" }}>
          That the purchase was wise, that the supplier is legitimate, or that anyone
          independent reviewed it. It says one person, named and holding a key, approved this
          exact amount to this exact merchant, and cannot later deny it. Everything else is
          somebody else's job.
        </p>

        <div className="row wrap" style={{ marginTop: "var(--s5)" }}>
          <a className="btn btn-go" href={`${base}/receipt`} rel="external">
            Open a receipt
          </a>
          <a className="btn" href="/tools/verify">
            Verify one yourself, offline
          </a>
        </div>
      </Card>

      <Gate
        client={client}
        source="receipt"
        title="Receipts for your own approvals"
        body="Every high-stakes action your agents take, with a link like this one you can forward to anyone."
        cta="Show me how"
      />
    </>
  );
}
